import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import axios from "axios";
import { buildApiUrl } from "../../lib/api";
import "./Bot.css";

const DISPLAY_DURATION = 5000;
const TYPE_INTERVAL_MS = 32;

const Bot = forwardRef(({ isLoggedIn, forceMessage }, ref) => {
    const [message, setMessage]                   = useState("");
    const [displayedMessage, setDisplayedMessage] = useState("");
    const [visible, setVisible]                   = useState(false);
    const [isTradeToast, setIsTradeToast]         = useState(false);
    const [toastType, setToastType]               = useState("info");
    const [isTradeLoading, setIsTradeLoading]     = useState(false);
    const [loadingMessage, setLoadingMessage]     = useState("Submitting trade…");
    const hideTimerRef  = useRef(null);
    const tradeResetRef = useRef(null);
    const typeTimerRef  = useRef(null);
    const typeRunRef    = useRef(0);

    const showMessage = (text, asTrade = false, type = "info") => {
        if (hideTimerRef.current)  clearTimeout(hideTimerRef.current);
        if (tradeResetRef.current) clearTimeout(tradeResetRef.current);
        if (typeTimerRef.current) {
            clearInterval(typeTimerRef.current);
            typeTimerRef.current = null;
        }
        typeRunRef.current += 1;
        setIsTradeLoading(false);
        setIsTradeToast(asTrade);
        setToastType(type);
        setMessage(text);
        setDisplayedMessage(asTrade ? text : "");
        setVisible(true);
        hideTimerRef.current = setTimeout(() => {
            setVisible(false);
            // Delay position reset until after the 0.45s fade-out finishes,
            // otherwise the container snaps to top-left while still visible.
            tradeResetRef.current = setTimeout(() => setIsTradeToast(false), 450);
        }, DISPLAY_DURATION);
    };

    // Loading state — show overlay + VV pulse immediately on execute press
    useEffect(() => {
        const handler = (event) => {
            if (hideTimerRef.current)  clearTimeout(hideTimerRef.current);
            if (tradeResetRef.current) clearTimeout(tradeResetRef.current);
            if (typeTimerRef.current) {
                clearInterval(typeTimerRef.current);
                typeTimerRef.current = null;
            }
            typeRunRef.current += 1;
            const nextMessage = event?.detail?.message || "Submitting trade…";
            setLoadingMessage(nextMessage);
            setMessage(nextMessage);
            setDisplayedMessage(nextMessage);
            setIsTradeLoading(true);
            setIsTradeToast(true);
            setToastType("info");
            setVisible(true);
        };
        window.addEventListener("savvy-trade-loading", handler);
        return () => window.removeEventListener("savvy-trade-loading", handler);
    }, []);

    // Trade toast events from execution components
    useEffect(() => {
        const handler = (e) => {
            const { message: msg, type } = e.detail;
            showMessage(msg, true, type || "info");
        };
        window.addEventListener("savvy-trade-toast", handler);
        return () => window.removeEventListener("savvy-trade-toast", handler);
    }, []);

    // Force message from parent (e.g. logout greeting)
    useEffect(() => {
        if (forceMessage) showMessage(forceMessage, false);
    }, [forceMessage]);

    // Status message from backend
    const fetchMessage = async (isCancelled = () => false) => {
        try {
            const response = await axios.get(
                `${buildApiUrl("/status-message")}?t=${Date.now()}`,
                { withCredentials: true }
            );
            if (isCancelled()) return;
            const fetched = response.data?.message;
            if (fetched && typeof fetched === "string") {
                showMessage(fetched, false);
            } else {
                showMessage("we in the badlands and can't connect to the saVVy server🙀", false);
            }
        } catch {
            if (isCancelled()) return;
            showMessage("Error: Failed to fetch message.", false);
        }
    };

    useEffect(() => {
        if (forceMessage) return;
        let cancelled = false;
        fetchMessage(() => cancelled);
        return () => { cancelled = true; };
    }, [isLoggedIn, forceMessage]);

    useImperativeHandle(ref, () => ({ refreshMessage: fetchMessage }));

    useEffect(() => {
        if (typeTimerRef.current) {
            clearInterval(typeTimerRef.current);
            typeTimerRef.current = null;
        }

        if (!message) {
            setDisplayedMessage("");
            return undefined;
        }

        if (isTradeToast || isTradeLoading) {
            setDisplayedMessage(message);
            return undefined;
        }

        const runId = ++typeRunRef.current;
        let index = 0;
        setDisplayedMessage("");

        typeTimerRef.current = setInterval(() => {
            if (typeRunRef.current !== runId) {
                clearInterval(typeTimerRef.current);
                typeTimerRef.current = null;
                return;
            }
            index += 1;
            setDisplayedMessage(message.slice(0, index));
            if (index >= message.length) {
                clearInterval(typeTimerRef.current);
                typeTimerRef.current = null;
            }
        }, TYPE_INTERVAL_MS);

        return () => {
            if (typeTimerRef.current) {
                clearInterval(typeTimerRef.current);
                typeTimerRef.current = null;
            }
            typeRunRef.current += 1;
        };
    }, [message, isTradeToast, isTradeLoading]);

    const containerClass = "bot-container" + (isTradeToast ? " bot-container--trade" : "");
    const bubbleClass    = "speech-bubble"  + (isTradeToast ? " speech-bubble--trade"  : "");
    const bubbleText = (isTradeLoading ? loadingMessage : displayedMessage) || " ";
    const reserveBubbleWidth = !isTradeToast && !isTradeLoading && Boolean(message);
    const showCursor = !isTradeToast && !isTradeLoading && visible;

    return (
        <>
            {isTradeToast && (
                <div
                    className="bot-trade-smoke"
                    style={{ opacity: (visible || isTradeLoading) ? 1 : 0 }}
                    aria-hidden="true"
                >
                    <div className="bot-trade-smoke-a" />
                    <div className="bot-trade-smoke-b" />
                </div>
            )}

            {isTradeLoading && (
                <div className="bot-vv-loader" aria-label="Submitting trade…">
                    <span className="bot-vv-loader-eye">V</span>
                    <span className="bot-vv-loader-eye">V</span>
                </div>
            )}

            <div className={containerClass}>
                <div
                    className="bot-speech-row"
                    style={{ opacity: visible ? 1 : 0, transition: "opacity 0.45s ease" }}
                >
                    {isTradeToast && <span className="bot-trade-dot" />}
                    <div className="bot-speech-inner">
                        <div className={bubbleClass}>
                            {reserveBubbleWidth ? (
                                <span className="speech-bubble-sizer" aria-hidden="true">
                                    {message}
                                </span>
                            ) : null}
                            <span className="speech-bubble-text">
                                {bubbleText}
                                {showCursor ? <span className="speech-bubble-cursor" aria-hidden="true" /> : null}
                            </span>
                        </div>
                        {isTradeToast && visible && !isTradeLoading && (
                            <div
                                key={message}
                                className="bot-loader-line"
                                style={{ animationDuration: DISPLAY_DURATION + "ms" }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </>
    );
});

export default Bot;
